import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { AuthButton } from '@/core/permissions/AuthButton';
import { CategoryForm } from '../shared/components/CategoryForm';
import { useCategoriesNew } from './hooks/useCategoriesNew';
import type { CategoryFormValues } from '../shared/types/categories.types';

export function CategoriesNewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const parentId = searchParams.get('parentId');
  const { lookups, createCategory, isSaving } = useCategoriesNew();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const handleSubmit = async (values: CategoryFormValues) => {
    try {
      await createCategory(values);
      toast.success('Tạo danh mục thành công');
      navigate('?tab=categories');
    } catch (error: any) {
      const message = error?.message || error?.details || 'Không thể tạo danh mục';
      toast.error(message);
    }
  };

  return (
    <div className="space-y-2 p-3">
      <section className="space-y-2 rounded-md border border-slate-100 bg-white p-3 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={() => setShowCancelConfirm(true)}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              <i className="ri-arrow-left-line" />
            </button>
            <div className="min-w-0">
              <h1 className="truncate text-lg font-semibold text-slate-900">Thêm danh mục mới</h1>
              <p className="truncate text-sm text-slate-500">Tạo cấu trúc phân loại cho hệ thống</p>
            </div>
          </div>
        </div>
      </section>

      <CategoryForm
        id="category-new-form"
        lookups={lookups}
        initialValues={{ parentId }}
        onSubmit={handleSubmit}
        isLoading={isSaving}
      />

      <ConfirmDialog
        isOpen={showCancelConfirm}
        title="Xác nhận hủy"
        message="Mọi dữ liệu vừa nhập sẽ bị xóa."
        confirmText="Thoát"
        cancelText="Tiếp tục"
        isDestructive
        onConfirm={() => navigate('?tab=categories')}
        onClose={() => setShowCancelConfirm(false)}
      />

      {/* Sticky Footer */}
      <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-end gap-3 border-t border-slate-200 bg-white p-4 shadow-lg lg:left-[240px]">
        <button
          type="button"
          onClick={() => setShowCancelConfirm(true)}
          disabled={isSaving}
          className="h-10 rounded-md border border-slate-200 bg-white px-6 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
        >
          Hủy bỏ
        </button>
        <AuthButton
          module="settings.categories"
          action="create"
          behavior="disable"
          form="category-new-form"
          type="submit"
          disabled={isSaving}
          className="flex h-10 items-center justify-center gap-2 rounded-md bg-emerald-600 px-6 text-sm font-medium text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
        >
          {isSaving ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" /> : <i className="ri-save-line" />}
          <span>{isSaving ? 'Đang lưu...' : 'Lưu danh mục'}</span>
        </AuthButton>
      </div>
    </div>
  );
}
